import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaTools,
  FaGuitar,
  FaTruck,
  FaWrench,
  FaCheck,
  FaArrowRight,
  FaPhoneAlt,
  FaChevronDown,
  FaCalendarCheck,
  FaCommentDots,
  FaCogs,
  FaSmile,
} from 'react-icons/fa';
import { GiGrandPiano } from 'react-icons/gi';
import { useSettings } from '../context/SettingsContext';
import './Services.css';

const services = [
  {
    id: 'piano-sales-rentals',
    tag: 'Sales & Rentals',
    title: 'Piano Sales & Rentals',
    icon: GiGrandPiano,
    image: '/images/piano-sales-rentals.jpg',
    description:
      'From elegant grand pianos to dependable upright models, we offer a curated selection of new and pre-owned acoustic and digital pianos. Whether you\'re furnishing a home, conservatory, or church, our team helps you find the perfect instrument with flexible rental plans available for short-term events and long-term lessons.',
    features: [
      'Yamaha, Kawai, Steinway, Roland, Korg & more',
      'New and certified pre-owned options',
      'Flexible monthly and yearly rental plans',
      'Rent-to-own arrangements available',
      'Trade-in and trade-up programs',
      'Free delivery within Lagos on purchases',
    ],
    cta: { label: 'Browse pianos', to: '/products?category=pianos' },
  },
  {
    id: 'piano-tuning',
    tag: 'Tuning & Maintenance',
    title: 'Piano Tuning & Maintenance',
    icon: FaTools,
    image: '/images/piano-tuning.jpg',
    description:
      'Pianos are living instruments — they breathe, settle, and respond to climate. Our certified tuners restore your piano to concert pitch and perform regular maintenance to keep every key responsive, every string singing, and every felt hammer in top condition. Recommended at least twice a year.',
    features: [
      'Standard tuning to A-440 concert pitch',
      'Pitch raise for severely flat pianos',
      'Action regulation and voicing',
      'Hammer reshaping and felt replacement',
      'Climate control consultation',
      'Maintenance contracts for schools and venues',
    ],
    cta: { label: 'Book a tuning', to: '/contact' },
  },
  {
    id: 'instruments-accessories',
    tag: 'Instruments & Accessories',
    title: 'Musical Instruments & Accessories Sales',
    icon: FaGuitar,
    image: '/images/instruments-accessories.jpg',
    imageFit: 'contain',
    description:
      'Beyond pianos, we stock a complete catalogue of professional instruments and gear — guitars, violins, saxophones, drum kits, studio monitors, microphones, and the essential accessories that round out your setup. Every product is sourced from trusted manufacturers and inspected before sale.',
    features: [
      'Acoustic, electric, classical & bass guitars',
      'Orchestral strings — violins, violas, cellos',
      'Brass & woodwinds — saxophones, trumpets, flutes',
      'Drum kits, cajons & hand percussion',
      'Studio gear — interfaces, monitors, microphones',
      'Stands, cases, cables, strings & metronomes',
    ],
    cta: { label: 'Shop instruments', to: '/products' },
  },
  {
    id: 'piano-movement',
    tag: 'Logistics',
    title: 'Piano Movement & Relocation',
    icon: FaTruck,
    image: '/images/piano-movement.jpg',
    description:
      'Moving a piano is a delicate operation — these instruments can weigh up to 500kg and contain over 7,000 working parts. Our trained movers use specialised equipment, padding, and rigging techniques to safely relocate your piano across the room or across the country, fully insured every step of the way.',
    features: [
      'Local Lagos and inter-state piano relocations',
      'Climate-controlled transport for premium models',
      'Stairs, lifts & narrow doorway specialists',
      'Disassembly and reassembly of grand pianos',
      'Full transit insurance coverage',
      'Post-move tuning to settle the instrument',
    ],
    cta: { label: 'Request a quote', to: '/contact' },
  },
  {
    id: 'piano-repair',
    tag: 'Restoration',
    title: 'Piano Repair & Restoration',
    icon: FaWrench,
    image: '/images/piano-repair.jpg',
    description:
      'From sticky keys to complete restoration of vintage instruments, our master technicians revive pianos of every age and brand. We diagnose, source authentic replacement parts, and rebuild components with traditional craftsmanship — bringing heirloom instruments back to performance condition for the next generation.',
    features: [
      'Action repair, regulation & voicing',
      'Soundboard, bridge & pinblock restoration',
      'Cabinet refinishing and polishing',
      'Complete rebuilds of vintage instruments',
      'String replacement and re-stringing',
      'Free initial diagnostic assessment',
    ],
    cta: { label: 'Schedule diagnosis', to: '/contact' },
  },
];

const processSteps = [
  { icon: FaCommentDots, title: 'Get in touch', description: 'Call, message, or submit our contact form to describe your needs.' },
  { icon: FaCalendarCheck, title: 'Book a consultation', description: 'We schedule a visit or showroom appointment at your convenience.' },
  { icon: FaCogs, title: 'Expert service', description: 'Our certified technicians or sales team deliver the work to your standards.' },
  { icon: FaSmile, title: 'Enjoy your music', description: 'Walk away with an instrument or service that exceeds expectations.' },
];

const faqs = [
  {
    q: 'How often should I tune my piano?',
    a: 'For most homes, twice a year is recommended — typically once at the start of the dry season and once during the rainy season. Newly purchased pianos and instruments used heavily for performance may need quarterly tunings.',
  },
  {
    q: 'Do you offer warranties on the instruments you sell?',
    a: 'Yes. New instruments come with the manufacturer\'s warranty, typically 5–10 years for pianos. Pre-owned pianos sold by Centre Techniques carry our own 12-month inspection warranty for peace of mind.',
  },
  {
    q: 'How much does it cost to move a piano?',
    a: 'Pricing depends on the piano size, distance, floor levels, and access difficulty. Local moves within Lagos typically start at ₦35,000 for upright pianos. Contact us with your specific details for an exact quote.',
  },
  {
    q: 'Can old or damaged pianos really be restored?',
    a: 'Absolutely. We\'ve restored instruments over 100 years old to concert-quality condition. After a free diagnostic visit we\'ll let you know honestly whether restoration is worthwhile or whether replacement makes more financial sense.',
  },
  {
    q: 'Do you deliver outside Lagos?',
    a: 'Yes, we serve clients across Nigeria. Inter-state delivery and movement are arranged through our trusted logistics partners with full insurance. Please contact us for specific pricing and timelines.',
  },
];

const Services = () => {
  const [openFaq, setOpenFaq] = useState(0);
  const { settings } = useSettings();

  return (
    <div className="services-page">
      {/* Hero */}
      <section className="services-hero">
        <div className="container services-hero-content">
          <span className="hero-eyebrow">Our Services</span>
          <h1>
            Everything for the <span>music maker</span>
          </h1>
          <p>
            From your first piano lesson to a concert hall restoration, Centre Techniques Pianos
            has been the trusted partner of musicians, teachers, schools, and houses of worship
            across Nigeria. Five core services, one promise: quality you can hear and feel.
          </p>
          <div className="services-hero-cta">
            <Link to="/contact" className="btn btn-primary btn-lg">
              Book a service <FaArrowRight />
            </Link>
            {settings?.contactPhone && (
              <a href={`tel:${settings.contactPhone}`} className="btn btn-secondary btn-lg">
                <FaPhoneAlt /> {settings.contactPhone}
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="services-stats">
        <div className="container services-stats-grid">
          <div className="stat-block">
            <strong>30+ yrs</strong>
            <span>Of experience</span>
          </div>
          <div className="stat-block">
            <strong>500+</strong>
            <span>Instruments serviced</span>
          </div>
          <div className="stat-block">
            <strong>100%</strong>
            <span>Insured movements</span>
          </div>
          <div className="stat-block">
            <strong>5★</strong>
            <span>Customer rating</span>
          </div>
        </div>
      </section>

      {/* Service rows */}
      <section className="services-list">
        <div className="container">
          <div className="section-intro">
            <h2>What we do best</h2>
            <p>Five specialised services, every one delivered by certified technicians who treat your instrument like their own.</p>
          </div>

          {services.map((service, index) => {
            const Icon = service.icon;
            const isReverse = index % 2 === 1;

            return (
              <div
                key={service.id}
                id={service.id}
                className={`service-row ${isReverse ? 'reverse' : ''}`}
              >
                <div className="service-content">
                  <span className="service-tag">{service.tag}</span>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <ul className="service-features">
                    {service.features.map((feat, i) => (
                      <li key={i}>
                        <FaCheck /> {feat}
                      </li>
                    ))}
                  </ul>
                  <Link to={service.cta.to} className="service-cta">
                    {service.cta.label} <FaArrowRight />
                  </Link>
                </div>

                <div className={`service-visual ${service.imageFit === 'contain' ? 'visual-contain' : ''}`}>
                  <span className="service-visual-num">0{index + 1}</span>
                  {service.image ? (
                    <img src={service.image} alt={service.title} className="service-visual-img" />
                  ) : (
                    <Icon className="service-visual-icon" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Process */}
      <section className="process-section">
        <div className="container">
          <div className="section-intro">
            <h2>How it works</h2>
            <p>Booking any service at Centre Techniques is a simple four-step journey.</p>
          </div>

          <div className="process-grid">
            {processSteps.map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={i} className="process-step">
                  <div className="process-step-num">
                    <Icon />
                  </div>
                  <h4>{step.title}</h4>
                  <p>{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <div className="container">
          <div className="section-intro">
            <h2>Frequently asked questions</h2>
            <p>Quick answers to what customers ask us most often.</p>
          </div>

          <div className="faq-list">
            {faqs.map((faq, i) => (
              <div key={i} className={`faq-item ${openFaq === i ? 'open' : ''}`}>
                <button
                  className="faq-question"
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  aria-expanded={openFaq === i}
                >
                  <span>{faq.q}</span>
                  <span className="faq-icon"><FaChevronDown /></span>
                </button>
                <div className="faq-answer">{faq.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="services-cta">
        <div className="container">
          <div className="services-cta-card">
            <h2>Ready to get started?</h2>
            <p>
              Whether you need a tune-up, a new instrument, or a careful relocation,
              our team is one message away. Get a free quote in 24 hours.
            </p>
            <div className="services-cta-buttons">
              <Link to="/contact" className="btn btn-primary btn-lg">
                Book a service <FaArrowRight />
              </Link>
              <Link to="/products" className="btn btn-secondary btn-lg">
                Browse our store
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
